// Feed loading shared by the startup load, the delayed secondary load and
// language switches. Requests for the same feed that are still in flight are
// shared, and loaded feeds are remembered so a language switch only fetches
// what is missing.

// feedKey :: Feed -> String
const feedKey = feed => feed.url_client;

/**
 * @param {(feed: any) => Promise<any>} fetchFeed
 * @param {(feed: any, data: any) => void} save
 */
export function createFeedLoader (fetchFeed, save) {
  const inFlight = new Map();
  const loadedAt = new Map();

  // load :: Feed -> Promise
  //    Always fetches (data refresh), unless the same feed is already in flight.
  function load (feed) {
    const key = feedKey(feed);
    if (inFlight.has(key)) return inFlight.get(key);

    const done = () => inFlight.delete(key);
    const request = fetchFeed(feed).then(
      data => {
        save(feed, data);
        loadedAt.set(key, Date.now());
        done();
      },
      error => {
        done();
        throw error;
      }
    );
    inFlight.set(key, request);
    return request;
  }

  // isLoaded :: [Feed] -> Bool
  const isLoaded = feeds => feeds.every(feed => loadedAt.has(feedKey(feed)));

  // ensure :: [Feed] -> Promise Bool
  //    Loads the feeds that were never loaded, resolves true if it had to.
  function ensure (feeds) {
    const missing = feeds.filter(feed => !loadedAt.has(feedKey(feed)));
    return Promise.all(missing.map(load)).then(() => missing.length > 0);
  }

  // loadUnlessLoadedSince :: (Feed, Number) -> Promise
  //    For scheduled loads: skips feeds a language switch already loaded
  //    after the schedule was made.
  function loadUnlessLoadedSince (feed, since) {
    const at = loadedAt.get(feedKey(feed));
    return at >= since ? Promise.resolve() : load(feed);
  }

  return { load, ensure, isLoaded, loadUnlessLoadedSince };
}
